import { useState } from 'react';
import { Grid, Button, Chip, Stack } from '@mui/material';
import { Send, CheckCircle, ErrorOutline, HourglassBottom } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { DataTable, type Column } from '../../components/DataTable';
import { whatsappMessagesApi } from '../../api/whatsapp.api';
import { useAuth } from '../../hooks/useAuth';

const n = (x = 0) => Number(x).toLocaleString('en-IN');

interface MessageRow { id: number; to_number: string; template_code: string | null; status: string; delivery_status: string | null; provider: string | null; created_at: string }

export function WhatsAppLogsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const dash = useQuery({ queryKey: ['whatsapp', 'dashboard'], queryFn: () => whatsappMessagesApi.dashboard() });
  const list = useQuery({ queryKey: ['whatsapp', 'messages', page, limit], queryFn: () => whatsappMessagesApi.list({ page, limit }) });
  const processMut = useMutation({ mutationFn: () => whatsappMessagesApi.process(), onSuccess: () => { qc.invalidateQueries({ queryKey: ['whatsapp'] }); } });

  const d = dash.data;
  const columns: Column<MessageRow>[] = [
    { key: 'to_number', label: 'To' },
    { key: 'template_code', label: 'Template', render: (r) => r.template_code ?? '—' },
    { key: 'provider', label: 'Provider', render: (r) => r.provider ?? '—' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'sent' ? 'success' : r.status === 'failed' ? 'error' : 'default'} /> },
    { key: 'delivery_status', label: 'Delivery', render: (r) => r.delivery_status ?? '—' },
  ];

  return (
    <>
      <PageHeader title="WhatsApp Hub" subtitle="Message logs and delivery status."
        action={hasPermission('whatsapp.read') && <Button startIcon={<Send />} variant="contained" onClick={() => processMut.mutate()} disabled={processMut.isPending}>Process Queue</Button>} />
      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={3}><StatCard gradient color="#1e40af" label="Total" value={n(d?.total)} icon={<Send />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard gradient color="#16a34a" label="Sent" value={n(d?.sent)} icon={<CheckCircle />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard gradient color="#d97706" label="Queued" value={n(d?.queued)} icon={<HourglassBottom />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard gradient color="#dc2626" label="Failed" value={n(d?.failed)} icon={<ErrorOutline />} /></Grid>
      </Grid>
      <Stack mb={1} />
      <DataTable
        columns={columns}
        rows={(list.data?.data ?? []) as unknown as MessageRow[]}
        loading={list.isLoading}
        page={page}
        limit={limit}
        total={list.data?.meta?.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
      />
    </>
  );
}
